// Works out whether a bulk import saved when the request itself failed
// without a clear answer (gateway timeout, dropped connection).
// The server saves an import in one database transaction, so if sampled
// rows are present the whole batch was saved.

const UNCLEAR_STATUSES = new Set([502, 503, 504]);
const SAMPLE = 10;

export function isUnclearFailure(error) {
  if (!error) return false;
  if (!error.response) return true; // timeout or network drop
  return UNCLEAR_STATUSES.has(error.response.status);
}

function sample(rows) {
  if (rows.length <= SAMPLE) return rows;
  const step = rows.length / SAMPLE;
  return Array.from({ length: SAMPLE }, (_, i) => rows[Math.floor(i * step)]);
}

// True when every sampled row is already in its account.
export async function importLanded(checkDuplicates, rows) {
  const byAccount = new Map();
  for (const row of rows) {
    if (!row.account_id) continue;
    if (!byAccount.has(row.account_id)) byAccount.set(row.account_id, []);
    byAccount.get(row.account_id).push(row);
  }
  if (byAccount.size === 0) return false;
  for (const [accountId, list] of byAccount) {
    const picked = sample(list);
    const res = await checkDuplicates(picked, accountId);
    const found = Array.isArray(res?.data?.duplicates) ? res.data.duplicates.length : 0;
    if (found < picked.length) return false;
  }
  return true;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Polls for a while: after a gateway timeout the server may still be finishing.
export async function waitForImport(checkDuplicates, rows, { tries = 8, delayMs = 5000 } = {}) {
  for (let i = 0; i < tries; i += 1) {
    try {
      if (await importLanded(checkDuplicates, rows)) return true;
    } catch {
      // keep polling; a single failed check proves nothing
    }
    if (i < tries - 1) await wait(delayMs);
  }
  return false;
}

// What to tell the user after a failed bulk-import request.
export async function explainImportFailure(error, rows, checkDuplicates, formatError) {
  const landed = isUnclearFailure(error) && await waitForImport(checkDuplicates, rows);
  const message = landed
    ? 'Import finished. The transactions were saved; the list will refresh.'
    : formatError(error, 'Failed to import transactions');
  return { landed, message };
}
